import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Disc, Plus, AlertCircle, ExternalLink, Image as ImageIcon, Edit, Music, Trash2, Archive, X, RotateCcw, Target } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { supabase } from '../../backend/services/supabase';
import { useArtists } from '../hooks/useArtists';
import { useReleases } from '../hooks/useReleases';
import { useContacts } from '../hooks/useContacts';
import type { Release, Artist } from '../../shared/types/entities';
import UnlinkedInbox from '../components/releases/UnlinkedInbox';
import UnifiedReleaseModal from '../components/releases/UnifiedReleaseModal';
import { ArchiveModal } from '../components/shared/ArchiveModal';
import { DeleteModal } from '../components/shared/DeleteModal';
import { RestoreModal } from '../components/shared/RestoreModal';
import { useAuth } from '../hooks/useAuth';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { toast } from 'sonner';
import { useCampaignCounts } from '../hooks/useCampaignCounts';

interface ReleaseWithArtist extends Release {
  artist: Artist;
}

interface SmartMatchSuggestion {
  query: string;
  matches: ReleaseWithArtist[];
  confidence: number;
  source: 'upload' | 'manual';
}

export default function Releases() {
  const { isAdmin } = useAuth();
  const { hasRole: effectiveHasRole } = useEffectivePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const { artists } = useArtists();
  const { contacts } = useContacts();
  const { 
    releases: hookReleases, 
    loading: hookLoading, 
    archiveRelease, 
    restoreRelease, 
    deleteRelease, 
    getArchivedReleases,
    refetch: refetchReleases
  } = useReleases();
  
  // State
  const [loading, setLoading] = useState(true);
  const [smartSuggestions, setSmartSuggestions] = useState<SmartMatchSuggestion[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');
  const [selectedRelease, setSelectedRelease] = useState<ReleaseWithArtist | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'archived'>('all');
  
  // Archive/Delete state
  const [archivedReleases, setArchivedReleases] = useState<ReleaseWithArtist[]>([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  const { releaseCounts } = useCampaignCounts();

  const getReleaseCampaignStats = (releaseId: string) => {
    const stats = releaseCounts[releaseId];
    return {
      active: stats?.active ?? 0,
      total: stats?.total ?? 0
    };
  };
  
  // Get context from URL params (for smart linking from other pages)
  const suggestedSong = searchParams.get('suggest_song');
  const suggestedArtist = searchParams.get('artist_id');
  const fromContext = searchParams.get('from'); // e.g., 'upload', 'reports'

  useEffect(() => {
    fetchReleases();
    
    // Handle smart suggestions from URL params
    if (suggestedSong && suggestedArtist) {
      handleSmartSuggestion(suggestedSong, suggestedArtist);
    }
    if (fromContext || suggestedSong) {
      setActiveTab('uploads');
    }
  }, []);

  // Fetch archived releases when archived tab is clicked
  useEffect(() => {
    if (activeTab === 'archived') {
      fetchArchivedReleases();
    }
  }, [activeTab]);

  const fetchReleases = async () => {
    try {
      setLoading(true);
      await refetchReleases();
    } catch (error) {
      console.error('Failed to fetch releases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedReleases = async () => {
    try {
      setArchivedLoading(true);
      const archived = await getArchivedReleases();
      setArchivedReleases(archived);
    } catch (error) {
      console.error('Failed to fetch archived releases:', error);
    } finally {
      setArchivedLoading(false);
    }
  };

  const handleSmartSuggestion = async (songName: string, artistId: string) => {
    // Smart matching algorithm
    const artistReleases = hookReleases.filter(r => r.artist_id === artistId);
    const matches = artistReleases.filter(release => 
      fuzzyMatch(release.title, songName) > 0.6
    );

    if (matches.length > 0) {
      setSmartSuggestions([{
        query: songName,
        matches,
        confidence: Math.max(...matches.map(m => fuzzyMatch(m.title, songName))) * 100,
        source: 'upload'
      }]);
    }
  };

  // Simple fuzzy matching algorithm
  const fuzzyMatch = (str1: string, str2: string): number => {
    const a = str1.toLowerCase().trim();
    const b = str2.toLowerCase().trim();
    
    if (a === b) return 1;
    if (a.includes(b) || b.includes(a)) return 0.8;
    
    // Simple character-based similarity
    const longer = a.length > b.length ? a : b;
    const shorter = a.length > b.length ? b : a;
    
    if (longer.length === 0) return 1;
    
    const editDistance = levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  };

  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  const releasesList = hookReleases;

  const fmtDuration = (ms?: number) => {
    if (!ms || ms <= 0) return '—';
    const sec = Math.round(ms / 1000);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleCreateRelease = () => {
    setSelectedRelease(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleEditRelease = (release: ReleaseWithArtist) => {
    setSelectedRelease(release);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleViewRelease = (release: ReleaseWithArtist) => {
    setSelectedRelease(release);
    setModalMode('view');
    setModalOpen(true);
  };

  const dismissSuggestion = (index: number) => {
    setSmartSuggestions(prev => prev.filter((_, i) => i !== index));
    
    // Clear URL params if all suggestions dismissed
    if (smartSuggestions.length === 1) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('suggest_song');
      newParams.delete('artist_id');
      newParams.delete('from');
      setSearchParams(newParams);
    }
  };

  // Archive/Delete handlers
  const handleArchiveClick = (release: ReleaseWithArtist, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedRelease(release);
    setShowArchiveModal(true);
  };

  const handleDeleteClick = (release: ReleaseWithArtist, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedRelease(release);
    setShowDeleteModal(true);
  };

  const handleRestoreClick = (release: ReleaseWithArtist, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedRelease(release);
    setShowRestoreModal(true);
  };

  const handleArchiveRelease = async () => {
    if (!selectedRelease) return;
    try {
      await archiveRelease(selectedRelease.id);
      setShowArchiveModal(false);
      setSelectedRelease(null);
      toast.success('Release archived');
    } catch (e) {
      console.error('Failed to archive release', e);
      toast.error('Failed to archive release');
    }
  };

  const handleRestoreRelease = async () => {
    if (!selectedRelease) return;
    try {
      await restoreRelease(selectedRelease.id);
      setShowRestoreModal(false);
      setSelectedRelease(null);
      // Remove from archived list
      setArchivedReleases(prev => prev.filter(r => r.id !== selectedRelease.id));
      toast.success('Release restored');
    } catch (e) {
      console.error('Failed to restore release', e);
      toast.error('Failed to restore release');
    }
  };

  const handleDeleteReleasePermanent = async () => {
    if (!selectedRelease) return;
    try {
      await deleteRelease(selectedRelease.id);
      setShowDeleteModal(false);
      setSelectedRelease(null);
      // Remove from archived list if we're on archived tab
      if (activeTab === 'archived') {
        setArchivedReleases(prev => prev.filter(r => r.id !== selectedRelease.id));
      } else {
        // Refresh the main releases list
        await refetchReleases();
      }
      toast.success('Release deleted');
    } catch (e) {
      console.error('Failed to delete release', e);
      toast.error('Failed to delete release');
    }
  };

  const handleDeleteRelease = async (release: ReleaseWithArtist, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await deleteRelease(release.id);
      // Refresh the releases list after deletion
      await refetchReleases();
      toast.success('Release deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete release');
    }
  };

  const handleThumbnailClick = (release: ReleaseWithArtist, e: React.MouseEvent) => {
    e.stopPropagation();
    handleViewRelease(release);
  };

  return (
    <div className="px-4 py-6 sm:px-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          icon={<Music className="h-8 w-8" />}
          title="Releases"
          actions={(
            <button
              onClick={handleCreateRelease}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              <Plus className="w-4 h-4 mr-2" /> Add
            </button>
          )}
        />

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700/80 mb-6">
          <nav className="flex flex-wrap gap-4 sm:gap-8" aria-label="Tabs">
            {[
              { id: 'all', label: 'All Releases' },
              { id: 'archived', label: 'Archived' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'all' | 'archived')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600 dark:text-purple-300'
                    : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'all' && (
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
        {loading || hookLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading releases...</p>
          </div>
        ) : releasesList.length === 0 ? (
          <div className="p-8 text-center">
            <Music className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No releases found
            </p>
            <button onClick={handleCreateRelease} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">Create your first release</button>
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Release</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Length</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Campaigns</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {releasesList.map((release) => (
                    <tr key={release.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => handleViewRelease(release)}>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={(e) => handleThumbnailClick(release, e)}
                          >
                            {release.artwork_url ? (
                              <img src={release.artwork_url} className="w-full h-full object-cover" />
                            ) : (
                              <ImageIcon className="w-5 h-5 m-2.5 text-gray-400" />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{release.title}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{release.artist?.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 capitalize">{release.release_type}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{release.release_date || '—'}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{fmtDuration(release.duration_ms)}</td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        {(() => {
                          const stats = getReleaseCampaignStats(release.id);
                          return (
                            <div className="flex items-center gap-2">
                              <Target className="h-4 w-4 text-blue-600" />
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{stats.active} active</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{stats.total} total campaigns</div>
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditRelease(release); }}
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            title="Edit release"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => handleArchiveClick(release, e)}
                            className="p-1.5 rounded hover:bg-orange-100 dark:hover:bg-orange-900/20 text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-200"
                            title="Archive release"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          {(effectiveHasRole('admin') || effectiveHasRole('super')) && (
                            <button
                              onClick={(e) => handleDeleteClick(release, e)}
                              className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                              title="Delete release"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {releasesList.map((release) => (
                <div
                  key={release.id}
                  className="p-4 flex flex-col gap-3"
                  onClick={() => handleViewRelease(release)}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-16 h-16 rounded-lg bg-gray-200 dark:bg-gray-700 overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={(e) => handleThumbnailClick(release, e)}
                    >
                      {release.artwork_url ? (
                        <img src={release.artwork_url} className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="w-6 h-6 m-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-base font-medium text-gray-900 dark:text-white truncate">{release.title}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{release.artist?.name}</div>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditRelease(release); }}
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            title="Edit release"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => handleArchiveClick(release, e)}
                            className="p-1.5 rounded hover:bg-orange-100 dark:hover:bg-orange-900/20 text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-200"
                            title="Archive release"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          {(effectiveHasRole('admin') || effectiveHasRole('super')) && (
                            <button
                              onClick={(e) => handleDeleteClick(release, e)}
                              className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                              title="Delete release"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm">
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <span className="font-medium text-gray-600 dark:text-gray-300">Type:</span>
                          <span className="capitalize text-gray-900 dark:text-gray-200">{release.release_type}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <span className="font-medium text-gray-600 dark:text-gray-300">Date:</span>
                          <span className="text-gray-900 dark:text-gray-200">{release.release_date || '—'}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <span className="font-medium text-gray-600 dark:text-gray-300">Length:</span>
                          <span className="text-gray-900 dark:text-gray-200">{fmtDuration(release.duration_ms)}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <span className="font-medium text-gray-600 dark:text-gray-300">Campaigns:</span>
                          {(() => {
                            const stats = getReleaseCampaignStats(release.id);
                            return (
                              <span className="text-gray-900 dark:text-gray-200">{stats.active} active / {stats.total} total</span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      )}

      {activeTab === 'archived' && (
        <>
          {archivedLoading ? (
            <div className="px-6 py-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading archived releases...</p>
            </div>
          ) : archivedReleases.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Archive className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No archived releases</h3>
              <p className="text-gray-600 dark:text-gray-400">Archived releases will appear here.</p>
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Release</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Length</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Campaigns</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {archivedReleases.map((release) => (
                      <tr key={release.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => handleViewRelease(release)}>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={(e) => handleThumbnailClick(release, e)}
                            >
                              {release.artwork_url ? (
                                <img src={release.artwork_url} className="w-full h-full object-cover" />
                              ) : (
                                <ImageIcon className="w-5 h-5 m-2.5 text-gray-400" />
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{release.title}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{release.artist?.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 capitalize">{release.release_type}</td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{release.release_date || '—'}</td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{fmtDuration(release.duration_ms)}</td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          {(() => {
                            const stats = getReleaseCampaignStats(release.id);
                            return (
                              <div className="flex items-center gap-2">
                                <Target className="h-4 w-4 text-blue-600" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">{stats.active} active</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">{stats.total} total campaigns</div>
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEditRelease(release); }}
                              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                              title="Edit release"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => handleRestoreClick(release, e)}
                              className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/20 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200"
                              title="Restore release"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={(e) => handleDeleteClick(release, e)}
                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                                title="Delete permanently"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {archivedReleases.map((release) => (
                  <div
                    key={release.id}
                    className="p-4 flex flex-col gap-3"
                    onClick={() => handleViewRelease(release)}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-16 h-16 rounded-lg bg-gray-200 dark:bg-gray-700 overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={(e) => handleThumbnailClick(release, e)}
                      >
                        {release.artwork_url ? (
                          <img src={release.artwork_url} className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-6 h-6 m-5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-base font-medium text-gray-900 dark:text-white truncate">{release.title}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{release.artist?.name}</div>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEditRelease(release); }}
                              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                              title="Edit release"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => handleRestoreClick(release, e)}
                              className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/20 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200"
                              title="Restore release"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={(e) => handleDeleteClick(release, e)}
                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
                                title="Delete permanently"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm">
                          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-300">Type:</span>
                            <span className="capitalize text-gray-900 dark:text-gray-200">{release.release_type}</span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-300">Date:</span>
                            <span className="text-gray-900 dark:text-gray-200">{release.release_date || '—'}</span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-300">Length:</span>
                            <span className="text-gray-900 dark:text-gray-200">{fmtDuration(release.duration_ms)}</span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-300">Campaigns:</span>
                            {(() => {
                              const stats = getReleaseCampaignStats(release.id);
                              return (
                                <span className="text-gray-900 dark:text-gray-200">{stats.active} active / {stats.total} total</span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Unified Release Modal */}
      <UnifiedReleaseModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedRelease(null);
        }}
        artists={artists}
        clients={contacts}
        release={selectedRelease || undefined}
        mode={modalMode}
        onCreated={(release) => {
          setModalOpen(false);
          refetchReleases();
        }}
        onUpdated={(release) => {
          setModalOpen(false);
          refetchReleases();
        }}
        onArtistCreated={(artist) => {
          // Artist was created, no need to do anything special
          // The modal will handle updating the form with the new artist
        }}
      />

      {/* Archive/Delete Modals */}
      <ArchiveModal
        isOpen={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false);
          setSelectedRelease(null);
        }}
        onArchive={handleArchiveRelease}
        entityName={selectedRelease?.title || ''}
        entityType="release"
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedRelease(null);
        }}
        onDelete={handleDeleteReleasePermanent}
        entityName={selectedRelease?.title || ''}
        entityType="release"
        isAdmin={effectiveHasRole('admin') || effectiveHasRole('super')}
      />

      <RestoreModal
        isOpen={showRestoreModal}
        onClose={() => {
          setShowRestoreModal(false);
          setSelectedRelease(null);
        }}
        onRestore={handleRestoreRelease}
        entityName={selectedRelease?.title || ''}
        entityType="release"
      />

      </div>
    </div>
  );
}
